import * as React from "react";
import "./styles.scss";
import { Button, Dropdown, DropdownItemProps, Icon, Input, Loader, Modal } from "semantic-ui-react";
import {
  CALENDAR_EVENT_SOURCE_METADATA,
  CalendarEventFilters,
  SOCIAL_NETWORK_METADATA,
  SOCIAL_NETWORK_POST_STATUS_NAMES,
  SocialNetworkPostStatus,
} from "@/interfaces";
import { CoachService, PlayersServices } from "@/services";
import pluralize from "pluralize";
import { isEqual } from "lodash";
import { useSocialCoachSession } from "@/utils";

type Props = {
  eventFilters: CalendarEventFilters;
  isLoading: boolean;
  onFiltersChanged: (filters: CalendarEventFilters) => void;
};

export const EventFilters: React.FC<Props> = ({ eventFilters, isLoading, onFiltersChanged }) => {
  const sourceOptions = Object.entries(CALENDAR_EVENT_SOURCE_METADATA).flatMap(([sourceType, metadata]) => {
    if (sourceType === "CONTENT_PAGE" || sourceType === "BLOG") return [];
    return [
      {
        key: sourceType,
        text: metadata.label,
        value: sourceType,
        icon: metadata.icon,
        className: sourceType.toLowerCase(),
      },
    ];
  });
  const socialNetworkOptions = Object.entries(SOCIAL_NETWORK_METADATA).flatMap(([socialNetworkId, metadata]) => {
    if (!metadata.forPosting) return [];
    return [
      {
        key: socialNetworkId,
        text: metadata.name,
        value: socialNetworkId,
        image: {
          src: metadata.icon,
          title: metadata.name,
          className: "socialNetworkIconOption",
          circular: true,
        },
      },
    ];
  });
  const statusOptions = [
    SocialNetworkPostStatus.POSTED,
    SocialNetworkPostStatus.PENDING,
    SocialNetworkPostStatus.FAILED,
    SocialNetworkPostStatus.SKIPPED_NO_CONNECTION,
  ].map(sn => ({
    key: sn,
    text: SOCIAL_NETWORK_POST_STATUS_NAMES[sn],
    value: sn,
  }));
  const [playerOptions, setPlayerOptions] = React.useState<DropdownItemProps[]>();
  const [branchOptions, setBranchOptions] = React.useState<DropdownItemProps[]>();
  const [regionOptions, setRegionOptions] = React.useState<DropdownItemProps[]>();

  const [displayAllFilters, setDisplayAllFilters] = React.useState<boolean>(false);
  const [unappliedFilters, setUnappliedFilters] = React.useState<CalendarEventFilters>({ ...eventFilters });

  const { isPlayer, coachId } = useSocialCoachSession();

  React.useEffect(() => {
    if (isPlayer) return;

    if (coachId) {
      CoachService.branches(coachId.toString())
        .then(response => {
          setBranchOptions(response.map(b => ({ key: b.branchId, text: b.name, value: b.branchId })));
        })
        .catch(e => {
          console.error(e);
          setBranchOptions([]);
        });
      CoachService.regions(coachId.toString())
        .then(response => {
          setRegionOptions(response.map(r => ({ key: r.regionId, text: r.name, value: r.regionId })));
        })
        .catch(e => {
          console.error(e);
          setRegionOptions([]);
        });
      PlayersServices.searchSubscribedAllPlayersByTag(coachId.toString())
        .then(response => {
          const playerAccounts = response.content
            .map(p => p.playerAccount)
            .sort((a, b) => {
              return (
                (a.firstName || "").localeCompare(b.firstName || "") ||
                (a.lastName || "").localeCompare(b.lastName || "")
              );
            });
          setPlayerOptions(
            playerAccounts.map(p => ({
              key: p.playerId,
              text: `${p.firstName} ${p.lastName}`,
              value: p.playerId,
            }))
          );
        })
        .catch(e => {
          console.error(e);
          setPlayerOptions([]);
        });
    } else {
      setBranchOptions([]);
      setRegionOptions([]);
      setPlayerOptions([]);
    }
  }, []);

  React.useEffect(() => {
    setUnappliedFilters({ ...eventFilters });
  }, [eventFilters]);

  const applyFilters = React.useCallback(() => {
    onFiltersChanged(unappliedFilters);
  }, [unappliedFilters]);

  const getMultiSelectDropdown = (
    options: DropdownItemProps[] | undefined,
    noun: string,
    filterType: keyof CalendarEventFilters,
    isUnappliedFilter: boolean = false
  ) => {
    const effectiveFilters = isUnappliedFilter ? unappliedFilters : eventFilters;
    const selectedFilters = effectiveFilters[filterType] as (string | number | boolean | undefined)[];
    var text: string | undefined = undefined;
    var placeholder: string | undefined = noun;
    if (selectedFilters.length > 0) {
      placeholder = undefined;
      if (selectedFilters.length === 1) {
        text = options?.find(el => el.value === selectedFilters[0])?.text as string;
      } else {
        text = `${selectedFilters.length} ${pluralize(noun, selectedFilters.length)} Selected`;
      }
    }

    const clearSelectedFilters = (event: any) => {
      event.stopPropagation();
      if (displayAllFilters) {
        setUnappliedFilters({ ...unappliedFilters, [filterType]: [] });
      } else {
        onFiltersChanged({ ...eventFilters, [filterType]: [] });
      }
    };

    const trigger = (
      <div className="customTrigger">
        <span>{text}</span>
        <Icon name="delete" onClick={clearSelectedFilters} />
      </div>
    );

    const [searchText, setSearchText] = React.useState<string>();
    const addSearchInput = (options?.length || 0) > 7;

    const dropdown = (
      <Dropdown
        className="calendarFiltersDropdown selection"
        loading={options === undefined}
        trigger={selectedFilters.length > 0 ? trigger : undefined}
        icon={selectedFilters.length > 0 ? "" : undefined}
        placeholder={placeholder}
        multiple
      >
        <Dropdown.Menu className="calendarFiltersDropdownMenu" onClick={(e: any) => e.stopPropagation()}>
          {addSearchInput && (
            <>
              <Dropdown.Divider />
              <Input
                placeholder="Search..."
                icon="search"
                iconPosition="left"
                className="calendarFiltersDropdownSearch"
                onKeyDown={(e: any) => (e.code === "Space" ? e.stopPropagation() : null)}
                onChange={e => setSearchText(e.target.value)}
              />
              <Dropdown.Divider />
            </>
          )}
          <Dropdown.Menu scrolling>
            {options?.map(option => {
              if (
                searchText &&
                option.text &&
                !option.text.toString().toLowerCase().includes(searchText.toLowerCase())
              ) {
                return null;
              }
              const isActive = selectedFilters.includes(option.value);
              return (
                <Dropdown.Item
                  {...option}
                  active={isActive}
                  onClick={() => {
                    const newSelectedFilters = isActive
                      ? selectedFilters.filter(el => el !== option.value)
                      : [...selectedFilters, option.value];
                    if (displayAllFilters) {
                      setUnappliedFilters({ ...unappliedFilters, [filterType]: newSelectedFilters });
                    } else {
                      onFiltersChanged({ ...eventFilters, [filterType]: newSelectedFilters });
                    }
                  }}
                />
              );
            })}
          </Dropdown.Menu>
        </Dropdown.Menu>
      </Dropdown>
    );

    if (isUnappliedFilter) {
      return (
        <div className="unappliedFilterField">
          <span>{noun}</span>
          {dropdown}
        </div>
      );
    } else {
      return dropdown;
    }
  };

  const getAllFiltersText = (eventFilters: CalendarEventFilters) => {
    let count = 0;
    Object.keys(eventFilters).forEach(k => {
      if (["date", "timePeriod", "coachId"].includes(k)) return;
      const filterValue = eventFilters[k as keyof CalendarEventFilters];
      if (Array.isArray(filterValue)) {
        count += filterValue.length;
      } else if (filterValue !== undefined) {
        count += 1;
      }
    });
    if (count > 0) return `All Filters (${count})`;
    else return "All Filters";
  };

  return (
    <div className="calendarFilters">
      {isPlayer && (
        <>
          {getMultiSelectDropdown(sourceOptions, "Source", "sourceTypes")}
          {getMultiSelectDropdown(socialNetworkOptions, "Social Network", "socialNetworkIds")}
        </>
      )}
      {!isPlayer && (
        <>
          {getMultiSelectDropdown(sourceOptions, "Source", "sourceTypes")}
          {getMultiSelectDropdown(socialNetworkOptions, "Social Network", "socialNetworkIds")}
          {getMultiSelectDropdown(playerOptions, "Player", "playerIds")}
          <Button secondary className={"rounded calendarEventFiltersBtn"} onClick={() => setDisplayAllFilters(true)}>
            {getAllFiltersText(eventFilters)}
          </Button>
          <Modal
            size={"small"}
            open={displayAllFilters}
            className="calendarEventFiltersModal"
            closeOnDimmerClick={false}
            closeOnEscape={false}
            onClose={() => setDisplayAllFilters(false)}
          >
            <Modal.Header>All Filters</Modal.Header>

            <Modal.Content>
              {getMultiSelectDropdown(sourceOptions, "Source", "sourceTypes", true)}
              {getMultiSelectDropdown(socialNetworkOptions, "Social Network", "socialNetworkIds", true)}
              {getMultiSelectDropdown(playerOptions, "Player", "playerIds", true)}
              {getMultiSelectDropdown(branchOptions, "Branch", "branchIds", true)}
              {getMultiSelectDropdown(regionOptions, "Region", "regionIds", true)}
              {getMultiSelectDropdown(statusOptions, "Status", "statuses", true)}
            </Modal.Content>

            <Modal.Actions>
              <Button
                secondary
                onClick={() => {
                  setUnappliedFilters({ ...eventFilters });
                  setDisplayAllFilters(false);
                }}
              >
                Cancel
              </Button>
              <Button
                primary
                disabled={isEqual(unappliedFilters, eventFilters)}
                onClick={() => {
                  applyFilters();
                  setDisplayAllFilters(false);
                }}
              >
                Apply Filters
              </Button>
            </Modal.Actions>
          </Modal>
        </>
      )}
      <Loader active={isLoading} inline size="small" />
    </div>
  );
};
